import type { ReactNode } from "react";
import clsx from "clsx";
import { formatCurrency } from "@/lib/money";
import { contractTypeLabel } from "@/lib/contractType";

export function contractStatusLabel(status: string): string {
  if (status === "active") return "Aktiv";
  if (status === "notice") return "Gekündigt";
  if (status === "terminated") return "Beendet";
  return status;
}

export function contractIsOpen(status: string): boolean {
  return status === "active" || status === "notice";
}

export function contractAmountLabel(contract: {
  billing_period_amount_gross?: string | null;
  monthly_amount?: string | null;
  resolved_period_amount_gross?: string | null;
}): string {
  if (contract.billing_period_amount_gross) return formatCurrency(contract.billing_period_amount_gross);
  if (contract.resolved_period_amount_gross) return formatCurrency(contract.resolved_period_amount_gross);
  if (contract.monthly_amount) return formatCurrency(contract.monthly_amount);
  return "aus Zuordnung";
}

export function ContractStatusBadge({ status }: { status: string }) {
  const tone =
    status === "active"
      ? "bg-status-green/15 text-status-green"
      : status === "notice"
        ? "bg-status-orange/15 text-status-orange"
        : "bg-fill-tertiary text-label-secondary";
  return (
    <span className={clsx("inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium", tone)}>
      {contractStatusLabel(status)}
    </span>
  );
}

export function ContractTypeBadge({ type }: { type: string }) {
  return (
    <span className="inline-flex rounded-full bg-accent-soft px-2.5 py-0.5 text-xs font-medium text-accent">
      {contractTypeLabel(type)}
    </span>
  );
}

export type ContractCreditFields = {
  superseded_on?: string | null;
  replacement_credit_days?: number | null;
  replacement_credit_gross?: string | null;
  replacement_credit_invoice_id?: string | null;
};

/** Offen, schon angelegt, oder keine Gutschrift zur Ablösung. */
export function contractCreditState(
  contract: ContractCreditFields,
): "available" | "issued" | null {
  const days = contract.replacement_credit_days ?? 0;
  const gross = Number(contract.replacement_credit_gross);
  const eligible =
    Boolean(contract.superseded_on) && days > 0 && Number.isFinite(gross) && gross > 0;
  if (!eligible) return null;
  return contract.replacement_credit_invoice_id ? "issued" : "available";
}

export function ContractCreditBadge({
  contract,
  issued = false,
}: {
  contract: ContractCreditFields;
  /** Nach dem Auslösen in derselben Sitzung, bevor die Liste neu lädt. */
  issued?: boolean;
}) {
  const state = contractCreditState(contract);
  const done = issued || state === "issued";
  if (!state && !done) return null;

  if (done) {
    return (
      <span className="inline-flex items-center whitespace-nowrap rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-800 dark:text-emerald-200">
        Gutschrift angelegt
      </span>
    );
  }

  const amount = formatCurrency(contract.replacement_credit_gross);
  const days = contract.replacement_credit_days ?? 0;
  const title = `${days} ${days === 1 ? "Tag" : "Tage"}, ${amount} brutto`;

  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-semibold text-emerald-950 ring-1 ring-inset ring-emerald-600/40 dark:bg-emerald-400/15 dark:text-emerald-100 dark:ring-emerald-300/50"
      title={title}
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500 dark:bg-emerald-300" aria-hidden />
      Gutschrift möglich
      <span className="font-medium tabular-nums text-emerald-800/80 dark:text-emerald-50/90">· {amount}</span>
    </span>
  );
}

export function ContractFormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-label">{title}</h3>
        {description ? <p className="mt-0.5 text-xs text-label-secondary">{description}</p> : null}
      </div>
      <div className="space-y-3 rounded-xl border border-separator bg-fill-tertiary/30 p-4">{children}</div>
    </section>
  );
}

export function ContractFact({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
}) {
  return (
    <div className="bg-card-elevated px-3.5 py-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-label-tertiary">{label}</p>
      <div className="mt-1 text-sm font-medium text-label">{value}</div>
      {hint ? <p className="mt-0.5 text-xs leading-snug text-label-secondary">{hint}</p> : null}
    </div>
  );
}

export function ContractChoice({
  checked,
  onChange,
  title,
  hint,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  title: string;
  hint: string;
  disabled?: boolean;
}) {
  return (
    <label
      className={clsx(
        "flex items-start gap-3 rounded-lg border border-separator bg-card px-3 py-2.5 text-sm",
        disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer",
      )}
    >
      <input
        type="checkbox"
        className="mt-0.5"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="font-medium text-label">{title}</span>
        <span className="mt-0.5 block text-xs text-label-secondary">{hint}</span>
      </span>
    </label>
  );
}
